// Accent token -> literal Tailwind class strings.
// IMPORTANT: every class must appear here as a full literal so Tailwind's
// content scanner emits it (dynamic `bg-neon-${x}` would be purged).

export type Accent = 'cyan' | 'purple' | 'green' | 'blue' | 'orange';

export interface AccentClasses {
  text: string;
  textHover: string;
  bgSoft: string;
  bgSofter: string;
  border: string;
  borderHover: string;
  dot: string;
  gradFrom: string;
}

export const ACCENT: Record<Accent, AccentClasses> = {
  cyan: {
    text: 'text-neon-cyan',
    textHover: 'group-hover:text-neon-cyan',
    bgSoft: 'bg-neon-cyan/10',
    bgSofter: 'bg-neon-cyan/5',
    border: 'border-neon-cyan/20',
    borderHover: 'hover:border-neon-cyan/20',
    dot: 'bg-neon-cyan',
    gradFrom: 'from-neon-cyan',
  },
  purple: {
    text: 'text-neon-purple',
    textHover: 'group-hover:text-neon-purple',
    bgSoft: 'bg-neon-purple/10',
    bgSofter: 'bg-neon-purple/5',
    border: 'border-neon-purple/20',
    borderHover: 'hover:border-neon-purple/20',
    dot: 'bg-neon-purple',
    gradFrom: 'from-neon-purple',
  },
  green: {
    text: 'text-neon-green',
    textHover: 'group-hover:text-neon-green',
    bgSoft: 'bg-neon-green/10',
    bgSofter: 'bg-neon-green/5',
    border: 'border-neon-green/20',
    borderHover: 'hover:border-neon-green/20',
    dot: 'bg-neon-green',
    gradFrom: 'from-neon-green',
  },
  blue: {
    text: 'text-neon-blue',
    textHover: 'group-hover:text-neon-blue',
    bgSoft: 'bg-neon-blue/10',
    bgSofter: 'bg-neon-blue/5',
    border: 'border-neon-blue/20',
    borderHover: 'hover:border-neon-blue/20',
    dot: 'bg-neon-blue',
    gradFrom: 'from-neon-blue',
  },
  orange: {
    text: 'text-orange-500',
    textHover: 'group-hover:text-orange-500',
    bgSoft: 'bg-orange-500/10',
    bgSofter: 'bg-orange-500/5',
    border: 'border-orange-500/20',
    borderHover: 'hover:border-orange-500/20',
    dot: 'bg-orange-500',
    gradFrom: 'from-orange-500',
  },
};

// Border at /30 on hover (credentials role cards). Literal for Tailwind scan.
export const BORDER_HOVER_30: Record<Accent, string> = {
  cyan: 'hover:border-neon-cyan/30',
  purple: 'hover:border-neon-purple/30',
  green: 'hover:border-neon-green/30',
  blue: 'hover:border-neon-blue/30',
  orange: 'hover:border-orange-500/30',
};

// Outline-CTA styling per accent (all literal for Tailwind scan).
export const OUTLINE: Record<Accent, { border: string; glow: string; shadow: string }> = {
  cyan: {
    border: 'border-neon-cyan/50',
    glow: 'from-neon-purple/20 to-neon-cyan/20',
    shadow: 'hover:shadow-[0_0_30px_-5px_rgba(0,255,255,0.4)]',
  },
  blue: {
    border: 'border-neon-blue/50',
    glow: 'from-neon-blue/20 to-neon-green/20',
    shadow: 'hover:shadow-[0_0_30px_-5px_rgba(0,191,255,0.4)]',
  },
  purple: {
    border: 'border-neon-purple/50',
    glow: 'from-neon-purple/20 to-neon-cyan/20',
    shadow: 'hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.4)]',
  },
  green: {
    border: 'border-neon-green/50',
    glow: 'from-neon-green/20 to-neon-cyan/20',
    shadow: 'hover:shadow-[0_0_30px_-5px_rgba(34,197,94,0.4)]',
  },
  orange: {
    border: 'border-orange-500/50',
    glow: 'from-orange-500/20 to-neon-cyan/20',
    shadow: 'hover:shadow-[0_0_30px_-5px_rgba(249,115,22,0.4)]',
  },
};

// Tech-pill hover accent (border + text on hover).
export const PILL_HOVER: Record<Accent, string> = {
  cyan: 'hover:border-neon-cyan/30 hover:text-neon-cyan',
  purple: 'hover:border-neon-purple/30 hover:text-neon-purple',
  green: 'hover:border-neon-green/30 hover:text-neon-green',
  blue: 'hover:border-neon-blue/30 hover:text-neon-blue',
  orange: 'hover:border-orange-500/30 hover:text-orange-500',
};

// Gradient blur wrappers behind image cards (two-stop).
export const GRADIENT: Record<string, string> = {
  'purple-cyan': 'from-neon-purple to-neon-cyan',
  'cyan-purple': 'from-neon-cyan to-neon-purple',
  'green-cyan': 'from-neon-green to-neon-cyan',
  'cyan-blue': 'from-neon-cyan to-neon-blue',
  'blue-green': 'from-neon-blue to-neon-green',
  'blue-cyan': 'from-neon-blue to-neon-cyan',
  'green-purple': 'from-neon-green to-neon-purple',
  'orange-cyan': 'from-orange-500 to-neon-cyan',
  'dark-purple': 'from-dark to-neon-purple',
  'white-cyan': 'from-white to-neon-cyan',
};

export type GradientKey = keyof typeof GRADIENT;
